/**
 * Pull a list of human-readable validation errors out of a thrown error from
 * a `ky` HTTP call. Two shapes are supported:
 *   - `{ errors: string[] }` — workflow validator emits plain strings like
 *     `Node 'mcp-query-1' (plugin=mcp-query): config.connectionRef Required`.
 *   - `{ errors: ZodIssue[] }` — nestjs-zod emits `{ path, message }` per
 *     field; rendered as `email: Invalid email`.
 * Anything that doesn't match either shape comes back as an empty array, in
 * which case callers should fall back to the bare `error.message`.
 *
 * Reads the body via `response.clone().json()` so callers can still inspect
 * the original response if they need to.
 */
export async function readApiValidationErrors(err: unknown): Promise<string[]> {
  if (!err || typeof err !== 'object') return [];
  const response = (err as { response?: Response }).response;
  if (!response || typeof response.clone !== 'function') return [];
  try {
    const body = (await response.clone().json()) as { errors?: unknown };
    if (!Array.isArray(body.errors)) return [];
    return body.errors.flatMap((entry) => {
      if (typeof entry === 'string') return [entry];
      if (entry && typeof entry === 'object') {
        const e = entry as { message?: unknown; path?: unknown };
        if (typeof e.message !== 'string') return [];
        const path = Array.isArray(e.path) ? e.path.filter((p) => p !== '').join('.') : '';
        return [path ? `${path}: ${e.message}` : e.message];
      }
      return [];
    });
  } catch {
    return [];
  }
}

export interface IApiErrorBody {
  code?: string;
  message?: string;
  retryAfterSec?: number;
}

/**
 * Read a coded error body (`{ code, message, retryAfterSec? }`) out of a
 * thrown `ky` error — the shape cortex uses for 409s such as
 * `schedule-disabled` / `fire-too-soon`. Returns null when the error carries
 * no readable JSON body, in which case callers fall back to `error.message`.
 */
export async function readApiErrorBody(err: unknown): Promise<IApiErrorBody | null> {
  if (!err || typeof err !== 'object') return null;
  const response = (err as { response?: Response }).response;
  if (!response || typeof response.clone !== 'function') return null;
  try {
    const body = (await response.clone().json()) as unknown;
    if (!body || typeof body !== 'object') return null;
    const b = body as { code?: unknown; message?: unknown; retryAfterSec?: unknown };
    return {
      ...(typeof b.code === 'string' ? { code: b.code } : {}),
      ...(typeof b.message === 'string' ? { message: b.message } : {}),
      ...(typeof b.retryAfterSec === 'number' ? { retryAfterSec: b.retryAfterSec } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Pull the lock violations out of a `409 managed-workflow-locked` response —
 * the API's answer when a save touches a locked node, field, edge or setting
 * of a Formant-managed workflow (or its schedule). One human-readable line
 * per violation; an empty array for any other error, so callers can chain it
 * after `readApiValidationErrors`. (DEV-17471.)
 */
export async function readManagedLockViolations(err: unknown): Promise<string[]> {
  if (!err || typeof err !== 'object') return [];
  const response = (err as { response?: Response }).response;
  if (!response || typeof response.clone !== 'function') return [];
  try {
    const body = (await response.clone().json()) as { code?: unknown; violations?: unknown };
    if (body.code !== 'managed-workflow-locked' || !Array.isArray(body.violations)) return [];
    return body.violations.flatMap((entry) => {
      if (!entry || typeof entry !== 'object') return [];
      const v = entry as { nodeKey?: unknown; field?: unknown };
      if (typeof v.field !== 'string') return [];
      const nodeKey = typeof v.nodeKey === 'string' && v.nodeKey ? `${v.nodeKey}.` : '';
      return [`${nodeKey}${v.field} is locked (managed by Formant)`];
    });
  } catch {
    return [];
  }
}

/**
 * Every structured error line a failed save can carry: per-field validation
 * errors, else managed-workflow lock violations.
 */
export async function readApiSaveErrors(err: unknown): Promise<string[]> {
  const validation = await readApiValidationErrors(err);
  if (validation.length > 0) return validation;
  return readManagedLockViolations(err);
}
